import { MonitoringAppliedFilters } from '../types';

const TIME_ZONE = 'America/Sao_Paulo';

export type TimelineGranularity = 'hora' | 'dia' | 'semana' | 'mes';
export type TimelineRange = Pick<MonitoringAppliedFilters, 'data_inicio' | 'data_fim_exclusiva'>;

const axisDateOptions: Intl.DateTimeFormatOptions = {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: 'short',
  year: '2-digit',
};
const axisFormatters = {
  hora: new Intl.DateTimeFormat('pt-BR', { ...axisDateOptions, hour: '2-digit' }),
  dia: new Intl.DateTimeFormat('pt-BR', axisDateOptions),
  semana: new Intl.DateTimeFormat('pt-BR', axisDateOptions),
  mes: new Intl.DateTimeFormat('pt-BR', { timeZone: TIME_ZONE, month: 'short', year: 'numeric' }),
};
const datePartsFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: TIME_ZONE,
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  hourCycle: 'h23',
  weekday: 'long',
});
const monthFormatter = new Intl.DateTimeFormat('pt-BR', { timeZone: TIME_ZONE, month: 'long', year: 'numeric' });

function getLocalDateParts(value: string | Date): Record<string, string> {
  const date = typeof value === 'string' ? new Date(value) : value;
  return Object.fromEntries(datePartsFormatter.formatToParts(date).map(part => [part.type, part.value]));
}

function calendarDate(value: string | Date): Date {
  const { year, month, day } = getLocalDateParts(value);
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
}

function calendarLabel(value: Date): string {
  return `${String(value.getUTCDate()).padStart(2, '0')}/${String(value.getUTCMonth() + 1).padStart(2, '0')}/${value.getUTCFullYear()}`;
}

function groupedPeriod(value: string, granularity: TimelineGranularity, range?: TimelineRange) {
  const start = calendarDate(value);
  const end = new Date(start);
  if (granularity === 'semana') end.setUTCDate(end.getUTCDate() + 6);
  if (granularity === 'mes') end.setUTCMonth(end.getUTCMonth() + 1, 0);
  const clippedStart = range ? new Date(Math.max(start.getTime(), calendarDate(range.data_inicio).getTime())) : start;
  const clippedEnd = range
    ? new Date(Math.min(end.getTime(), calendarDate(new Date(Date.parse(range.data_fim_exclusiva) - 1)).getTime()))
    : end;
  return {
    partial: clippedStart.getTime() !== start.getTime() || clippedEnd.getTime() !== end.getTime(),
    label: `${calendarLabel(clippedStart)} a ${calendarLabel(clippedEnd)}`,
  };
}

export function formatTimelineAxis(value: string, granularity: TimelineGranularity): string {
  return axisFormatters[granularity].format(new Date(value)).replace('.', '');
}

export function formatTimelineTooltip(value: string, granularity: TimelineGranularity, range?: TimelineRange): string {
  const parts = getLocalDateParts(value);
  const date = `${parts.day}/${parts.month}/${parts.year}`;

  if (granularity === 'hora') return `${date} às ${parts.hour}h`;
  if (granularity === 'dia') return `${parts.weekday}, ${date}`;
  if (granularity === 'semana') return `Semana de ${groupedPeriod(value, granularity, range).label}`;

  const month = monthFormatter.format(new Date(value));
  const period = groupedPeriod(value, granularity, range);
  return `${month}${period.partial ? ` (${period.label})` : ''}`;
}
